import {useTranslation} from '@/translations/i18n.tsx'
import {ChangeEvent, FC, ReactNode, useState} from 'react'
import {TreatmentRequestSmall} from '@/features/treatment-requests/types.ts'
import {DoctorTherapistSelectionModal} from '@/features/treatment-requests/components/doctor-therapist-selection-modal/DoctorTherapistSelectionModal.tsx'
import {useTreatmentRequestGroups} from '@/features/treatment-requests/services/useTreatmentRequestGroups.ts'
import {TherapistDoctorCard} from '@/features/treatment-requests/components/therapist-doctor-card/TherapistDoctorCard'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {debounce, errorHandler} from '@utilities/helpers.ts'
import {useStoreTeam} from '../../teams/queries/useStoreTeam'
import dayjs from 'dayjs'

export interface TherapistDoctorCellProps {
    isReadOnly?: boolean
    trigger: () => ReactNode
    treatmentRequest: TreatmentRequestSmall
    group: 'therapist' | 'referring_doctor'
}

export const TratmentRequestTherapistDoctorSelector: FC<TherapistDoctorCellProps> = ({
    isReadOnly,
    trigger,
    treatmentRequest,
    group
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false)

    return (
        <>
            <div
                style={{pointerEvents: isReadOnly ? 'none' : 'auto'}}
                onClick={event => {
                    event.stopPropagation()
                    setIsModalOpen(!isReadOnly)
                }}
            >
                {trigger()}
            </div>
            {isModalOpen && (
                <TratmentRequestTherapistDoctorSelectorModalBody
                    treatmentRequest={treatmentRequest}
                    isModalOpen={isModalOpen}
                    setIsModalOpen={setIsModalOpen}
                    group={group}
                />
            )}
        </>
    )
}

interface TratmentRequestTherapistDoctorSelectorModalBodyProps {
    treatmentRequest: TreatmentRequestSmall
    isModalOpen: boolean
    setIsModalOpen: (isModalOpen: boolean) => void
    group: 'therapist' | 'referring_doctor'
}

const TratmentRequestTherapistDoctorSelectorModalBody: FC<TratmentRequestTherapistDoctorSelectorModalBodyProps> = ({
    treatmentRequest,
    isModalOpen,
    setIsModalOpen,
    group
}) => {
    const {t} = useTranslation()
    const [dateRange, setDateRange] = useState<[string, string]>([
        dayjs().startOf('week').format('YYYY-MM-DD'),
        dayjs().endOf('week').format('YYYY-MM-DD')
    ])

    const consultants =
        group == 'therapist'
            ? treatmentRequest.therapists ?? []
            : treatmentRequest.referringDoctor
              ? [treatmentRequest.referringDoctor]
              : []

    const [selectedConsultants, setSelectedConsultants] = useState<string[]>(
        consultants.map(consultant => consultant.id)
    )
    const treatmentRequestGroupsQuery = useTreatmentRequestGroups({
        treatmentRequestId: treatmentRequest.id,
        options: {enabled: isModalOpen},
        group,
        startDate: treatmentRequest.sector == 'outpatient' ? dateRange[0] : undefined,
        endDate: treatmentRequest.sector == 'outpatient' ? dateRange[1] : undefined
    })

    const onSearchTherapistDoctor = debounce((event: ChangeEvent<HTMLInputElement>) => {
        treatmentRequestGroupsQuery.setSearch(event.target.value)
    }, 1000)

    const {mutate: storeTeam, isPending} = useStoreTeam(treatmentRequest.id, {
        onSuccess: () => {
            queryClient.invalidateQueries({queryKey: [QUERY_KEYS.TREATMENT_REQUESTS]})
            setIsModalOpen(false)
        },
        onError: errorHandler
    })

    return (
        <DoctorTherapistSelectionModal
            onConfirm={() => {
                storeTeam({
                    isAReserve: false,
                    teams: [
                        {
                            group,
                            users: selectedConsultants
                        }
                    ]
                })
            }}
            dateRange={dateRange}
            setDateRange={setDateRange}
            isConfirmLoading={isPending}
            isLoading={treatmentRequestGroupsQuery.isPending}
            isError={treatmentRequestGroupsQuery.isError}
            selectionList={treatmentRequestGroupsQuery.data ?? []}
            renderSelectionListItem={dataItem => (
                <TherapistDoctorCard
                    id={dataItem.id}
                    availableSlots={dataItem.availableSlots ?? 0}
                    onCheck={isChecked => {
                        if (isChecked) {
                            if (group == 'therapist') {
                                setSelectedConsultants(prevState => [...prevState, dataItem.id])
                            } else {
                                setSelectedConsultants([dataItem.id])
                            }
                        } else {
                            setSelectedConsultants(prevState =>
                                prevState.filter(selectedConsultant => selectedConsultant != dataItem.id)
                            )
                        }
                    }}
                    distance={dataItem.distance}
                    contacts={dataItem.contacts}
                    disabled={false}
                    fullName={dataItem.fullName}
                    checked={selectedConsultants.includes(dataItem.id)}
                    priCount={dataItem.priCount ?? 0}
                    specializationName={dataItem.specialization.name}
                />
            )}
            onSearch={onSearchTherapistDoctor}
            treatmentRequest={treatmentRequest}
            title={group == 'therapist' ? t('commons:therapist') : t('treatment_requests:project_referring_doctor')}
            onClose={() => setIsModalOpen(false)}
        />
    )
}
